import { Checkbox, Collapse, Grid, Typography } from '@material-ui/core';
import React, { FunctionComponent, useEffect } from 'react';
import styles from './AccessControlSecurityRuleAccordion.Styles';
import RadioButtonGroup from '../../generic-components/radio-button-group/RadioButtonGroup.Component';
import { permissions } from '../access-control-security-profiles-add-edit/AccessControlSecurityProfilesAddEdit.Component';
import ExpandMore from '@material-ui/icons/ExpandMore';
import SecurityRuleDataModel from '../../../data-models/admin/access-control/SecurityRule.DataModel';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { isReadOnlySecurityRule } from '../../../support/constants/SecurityRules.Constants';

const applyPermissionValue = (rule: SecurityRuleDataModel, permission: string, value: boolean) => {
    if (isReadOnlySecurityRule(rule.code) && permission !== 'read') {
        rule[permission] = false;
        return;
    }
    rule[permission] = value;
};

interface AccessControlSecurityRuleAccordionProps {
    rule: SecurityRuleDataModel;
    handleParentChangeRulePermission: (securityProfie: SecurityRuleDataModel, parentId: number) => void;
    changeParentRadioButton?: () => void;
    readOnly?: boolean;
}

export const AccessControlSecurityRuleAccordion: FunctionComponent<AccessControlSecurityRuleAccordionProps> = ({
    rule,
    handleParentChangeRulePermission,
    changeParentRadioButton,
    readOnly = false,
}) => {
    const classes = styles();
    const [expanded, setExpanded] = React.useState<boolean>(false);
    const [radioButtonGroupValue, setRadioButtonGroupValue] = React.useState<'none' | 'full' | 'partial'>(null);

    const isReadOnlyRule = isReadOnlySecurityRule(rule.code);

    const accessOptions = [
        { value: 'none', label: 'None', disabled: false },
        { value: 'full', label: 'Full', disabled: false },
        { value: 'partial', label: 'Partial', disabled: true },
    ];

    useEffect(() => {
        autoControlAccessRadioButtons();
    }, [rule, rule.children, rule.create, rule.update, rule.read, rule.delete]);

    const toggleOpen = () => {
        setExpanded(!expanded);
    };

    const handleChangeRulePermission = (ruleName: string, isParent: boolean, newValue: boolean = null) => {
        const newRule = { ...rule };
        applyPermissionValue(newRule, ruleName, newValue ?? !newRule[ruleName]);

        if (isParent) {
            const childValue = newValue ?? !!newRule[ruleName];
            for (const child of newRule.children) {
                applyPermissionValue(child, ruleName, childValue);
            }
        }

        handleParentChangeRulePermission(newRule, newRule.parentId);

        if (changeParentRadioButton !== undefined) {
            changeParentRadioButton();
        }
    };

    const applyAllPermissions = (target: SecurityRuleDataModel, value: boolean) => {
        for (const permission of permissions) {
            applyPermissionValue(target, permission.toLowerCase(), value);
        }
    };

    const massUpdateChildren = (rules: SecurityRuleDataModel[], value: boolean) => {
        for (let currentRule of rules) {
            applyAllPermissions(currentRule, value);
            if (currentRule.children?.length > 0) {
                massUpdateChildren(currentRule.children, value);
            }
        }
        return false;
    };

    const massUpadteRulePermissions = (newValue: boolean) => {
        const newRule = { ...rule };
        applyAllPermissions(newRule, newValue);

        massUpdateChildren(newRule.children, newValue);

        handleParentChangeRulePermission(newRule, newRule.parentId);

        if (changeParentRadioButton !== undefined) {
            changeParentRadioButton();
        }
    };

    const autoControlAccessRadioButtons = () => {
        if (isReadOnlyRule) {
            return setRadioButtonGroupValue(rule.read ? 'full' : 'none');
        }

        // The radio mirrors the four checkboxes on this same row. Parent rows carry the aggregated tri-state
        // values that updateParentRule derives from their children; walking children again with a separate
        // "full access" heuristic disagrees with those checkboxes and mislabels rows such as Service Set Up.
        const isFullAccess = !!(rule.read && rule.create && rule.update && rule.delete);
        const isNoneAccess =
            rule.read === false &&
            rule.create === false &&
            rule.update === false &&
            rule.delete === false;

        if (isFullAccess) {
            return setRadioButtonGroupValue('full');
        }

        if (isNoneAccess) {
            return setRadioButtonGroupValue('none');
        }

        return setRadioButtonGroupValue('partial');
    };

    const handleChangeRadioButtonGroup = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly) return;
        if (event.target.value === 'none') {
            setRadioButtonGroupValue('none');
            permissions.forEach((p) => {
                massUpadteRulePermissions(false);
            });
        } else if (event.target.value === 'full') {
            setRadioButtonGroupValue('full');
            permissions.forEach((p) => {
                massUpadteRulePermissions(true);
            });
        }
    };

    return (
        <Grid container item xs={12}>
                <Grid
                    container
                    item
                    xs={12}
                    alignItems="center"
                    className={classes.gridContainer}
                    style={{ backgroundColor: rule.children.length > 0 ? '#eee' : '#fff' }}
                >
                    {rule.children.length > 0 ? (
                        <Grid item container xs={3} className={classes.sidebarTitle} onClick={toggleOpen}>
                            <Grid item className={classes.parentTitleIcon}>
                                <ExpandMore color="inherit" />
                            </Grid>
                            <Grid item>
                                <Typography variant="subtitle2">{rule.name}</Typography>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid item xs={3}>
                            <Typography>{rule.name}</Typography>
                        </Grid>
                    )}

                    <Grid item xs={3}>
                        <RadioButtonGroup
                            value={radioButtonGroupValue}
                            labelPlacement="end"
                            onChange={(e) => {
                                handleChangeRadioButtonGroup(e);
                            }}
                            radioButtons={accessOptions}
                            readOnly={readOnly}
                        />
                    </Grid>
                    {permissions.map((p) => {
                        const permission = p.toLowerCase();
                        const value = rule[permission];
                        const hidePermission = isReadOnlyRule && permission !== 'read';

                        return (
                            <Grid key={p + rule.securityRuleId + radioButtonGroupValue} item xs={1} justify="center">
                                {!hidePermission &&
                                    (readOnly ? (
                                        <div> {value ? <CheckIcon /> : <CloseIcon />} </div>
                                    ) : (
                                        <Checkbox
                                            color="primary"
                                            indeterminate={value === null}
                                            checked={value}
                                            onClick={() =>
                                                handleChangeRulePermission(permission, rule.children.length > 0)
                                            }
                                        />
                                    ))}
                            </Grid>
                        );
                    })}
                </Grid>
                {rule.children.length > 0 && (
                    <Collapse
                        style={{
                            flexGrow: 1,
                        }}
                        in={expanded}
                    >
                        {rule.children.map((child) => {
                            return (
                                <AccessControlSecurityRuleAccordion
                                    key={child.securityRuleId}
                                    rule={child}
                                    handleParentChangeRulePermission={handleParentChangeRulePermission}
                                    changeParentRadioButton={autoControlAccessRadioButtons}
                                    readOnly={readOnly}
                                />
                            );
                        })}
                    </Collapse>
                )}
            </Grid>
    );
};
